import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpErrorResponse,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';

import { ErrorMessage, MessagesHttpInterceptorBase } from '@library/common/http';
import { AuthService } from '@library/modules/auth';
import { ErrorMessageService } from '@library/core/services';

@Injectable()
export class MessagesHttpInterceptor extends MessagesHttpInterceptorBase {
   constructor(
      private auth: AuthService,
      private error: ErrorMessageService,
      private router: Router,
   ) {
      super();
   }

   handle( messages: ErrorMessage[], processId?: string ): void {
      if ( !messages || !messages.length ) { return; }

      messages.map( message => this.error.add( message ));

      // console.log( 'message handler messages', messages );

      messages.forEach( message => {
         // console.log( 'checking code', message );

         switch ( message.code ) {
            // Version stuff
            case 'VERSION_EXPIRED':
               const version = sessionStorage.getItem( 'ev' );
               if ( !version || version !== message.message ) {
                  sessionStorage.setItem( 'ev', message.message );
                  this.error.reset();
                  location.reload();
               }

               return;

            // User session stuff
            case 'USER_SESSION_EXPIRED':
            case 'USER_LOCKED':
               this.error.reset();
               this.error.add( message );
               if ( [ '/login', '/logout', '/lock' ].indexOf( this.router.url ) === -1 ) {
                  this.auth.logout().catch( () => {} );
               }

               return;
            case 'USER_NOT_FOUND':
            case 'USER_IS_INACTIVE':
            case 'USER_SESSION_DOES_NOT_EXIST':
            case 'UNAUTHORIZED_USER':
               this.auth.logout().catch( () => {} );

               return;

            // Gatekeeper stuff
            case 'TOU_NOT_READ':
            case 'TC_NOT_READ':
               this.router.navigate([ 'termsagree' ]);

               return;
            case 'USER_REQUIRED_TO_RESET_PASSWORD':
               this.router.navigate([ 'passwordreset' ]);

               return;

            // Misc problems
            case 'MALFORMED_PAYLOAD':
            case 'TEMPORARY_DATABASE_FAILURE':
            case 'UNEXPECTED_EXCEPTION':
               this.router.navigate([ 'error' ], { state: { processId } });

               return;
         }
      });
   }

   handleError( response: HttpErrorResponse, processId?: string ): void {
      const status = response?.status ?? 0;

      // Authentication failures are recoverable. Now that gateway 401/403s carry CORS
      // headers (see GatewayResponse config), the browser delivers them as real statuses
      // instead of an opaque status-0 network error. Send the user back through login —
      // matching the USER_SESSION_EXPIRED handling above — instead of stranding them on a
      // generic "Internal Server Error" page (the symptom that produced the login loop).
      if ( status === 401 || status === 403 ) {
         if ( [ '/login', '/logout', '/lock' ].indexOf( this.router.url ) === -1 ) {
            this.auth.logout().catch( () => {} );
         }

         return;
      }

      this.error.add({
         type: 'error',
         code: status ? status.toString() : 500,
         message: status ? status + ' ' + response.statusText : 'Internal Server Error',
      } as ErrorMessage );

      this.router.navigate([ 'error' ], { state: { errors: this.error.messages.getValue(), processId } });
   }
}

export let messagesHttpInterceptor = {
   provide: HTTP_INTERCEPTORS,
   useClass: MessagesHttpInterceptor,
   multi: true
};
